// filepath: resources/assets/front/js/app.js

import '../css/app.css';
import '@fontsource-variable/bricolage-grotesque';
import '@fontsource-variable/jetbrains-mono';

import htmx from 'htmx.org';
import Alpine from 'alpinejs';
import focus from '@alpinejs/focus';                    // ← ajout
import lightbox from 'alpine-tailwind-lightbox';   
import 'htmx-ext-head-support';
import LazyLoad from 'vanilla-lazyload';
import './components/pattern-showcase.js';
import 'prismjs';
import 'prismjs/plugins/autoloader/prism-autoloader';
import 'prismjs/themes/prism-tomorrow.css';

import { initHTMX }                                                      from './config/htmx.js';
import { initSlider }                                                     from './components/slider.js';
import { initGallery, getGallery, getAllGalleries, refreshAllGalleries }  from './components/gallery.js';
import { initAccordion, getAccordion, getAllAccordions, refreshAccordions } from './components/accordion.js';
import { initMobileMenu }                                                 from './components/mobile-menu.js';
import { initSmoothScrolling }                                            from './components/smooth-scroll.js';
import { initForms }                                                      from './components/forms.js';
import { initNotifications }                                              from './components/notifications.js';
import { initAOS, AOSConfig, resetAOSForRestore }                         from './components/aos.js';
import { showLoading, hideLoading }                                       from './utils/loading.js';
import { showNotification }                                                 from './utils/helpers.js';
import cookieConsent                                                        from './utils/CookieConsentManager.js';

// ── Alpine components ─────────────────────────────────────────────
import navBar from './alpine/navBar.js';
import siteSearch from './alpine/siteSearch.js';

window.htmx   = htmx;
window.Alpine = Alpine;

cookieConsent.init();
window.cookieConsentManager = cookieConsent; // expose pour les boutons PHP

// Enregistrer les composants Alpine AVANT Alpine.start()
document.addEventListener('alpine:init', () => {
    Alpine.data('navBar', navBar);
    Alpine.data('siteSearch', siteSearch);
});

Alpine.plugin(focus);          // requis par alpine-tailwind-lightbox
Alpine.plugin(lightbox);

Alpine.start();

// ─── LazyLoad ─────────────────────────────────────────────────────
//
// Stratégie :
//   • Sélecteur `.lazy`  → posé automatiquement par ImageGenerator sur toutes
//     les images non-LCP (isLCP=false et priority≠high).
//   • Les images LCP / priorité haute gardent leur src= natif et ne passent
//     jamais par ici.
//   • callback_loaded : retire la classe `lazy` et joue le fade-in CSS.
//   • Sur HTMX boosted (SPA-like) : lazyLoadInstance.update() re-scanne le DOM.
//
let lazyLoadInstance = null;

function initLazyLoad() {
    if (lazyLoadInstance) {
        // Re-scan : images ajoutées par HTMX swap ou pagination
        lazyLoadInstance.update();
        return;
    }

    lazyLoadInstance = new LazyLoad({
        // Sélecteur par défaut de vanilla-lazyload
        elements_selector: '.lazy',

        // Combien de pixels avant d'entrer dans le viewport on commence à charger.
        // 300px = l'image est prête avant que l'utilisateur la voie.
        threshold: 300,

        // Classe CSS posée sur l'<img> pendant le chargement (optionnel)
        class_loading: 'lazy-loading',

        // Classe posée une fois chargé (on l'utilise pour le fade-in)
        class_loaded: 'lazy-loaded',

        // Classe posée en cas d'erreur réseau
        class_error: 'lazy-error',

        // Callback une fois l'image chargée
        callback_loaded: (el) => {
            // Le fade-in est géré en CSS via .lazy-loaded
            // On peut aussi logger en dev :
            // console.debug('[LazyLoad] loaded', el.dataset.src ?? el.src);
        },

        // Callback erreur : on peut afficher un placeholder de fallback
        callback_error: (el) => {
            el.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='14' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EImage indisponible%3C/text%3E%3C/svg%3E";
        },
    });

    window.lazyLoadInstance = lazyLoadInstance;
}

// ─── Exports publics ──────────────────────────────────────────────
window.App = {
    environment: import.meta.env.MODE,
    version: __APP_VERSION__,
    htmx,
    showLoading,
    hideLoading,
    showNotification,
    gallery:   { get: getGallery,   getAll: getAllGalleries,  refresh: refreshAllGalleries },
    accordion: { get: getAccordion, getAll: getAllAccordions, refresh: refreshAccordions },
    // Exposé pour usage manuel si besoin (ex: après un AJAX custom)
    lazyLoad:  { update: () => lazyLoadInstance?.update() },
};

if (import.meta.hot) {
    import.meta.hot.accept();
}

// ─── Boot — Phase 1 : CRITIQUE (avant LCP) ────────────────────────
document.addEventListener('DOMContentLoaded', () => {
    initHTMX();                  // navigation HTMX, boost
    initMobileMenu(document);    // header interactif
    initLazyLoad();              // les images doivent commencer à charger
});

// ─── Boot — Phase 2 : NON-CRITIQUE (après LCP) ────────────────────
window.addEventListener('load', () => {
    const idle = (cb) => {
        if ('requestIdleCallback' in window) {
            requestIdleCallback(cb, { timeout: 2000 });
        } else {
            setTimeout(cb, 100);
        }
    };

    // Tout ce qui suit ne bloque plus LCP/FCP — exécuté pendant les temps morts
    idle(() => initSmoothScrolling(document));
    idle(() => initForms(document));
    idle(() => initNotifications(document));
    idle(() => initSlider(document));
    idle(() => initGallery(document));
    idle(() => initAccordion(document));
    idle(() => initHTMXComponents(document));
    idle(() => initAOS());        // AOS déjà optimisé en interne mais double safety
});

// ─── Init page — utilisée par HTMX swap & manuellement ───────────
function initPage(root = document) {
    initMobileMenu(root);
    initSmoothScrolling(root);
    initForms(root);
    initNotifications(root);
    initSlider(root);
    initGallery(root);
    initAccordion(root);
    initHTMXComponents(root);
}

window.App.init        = () => { initPage(document); initLazyLoad(); };
window.App.initPage    = initPage;
window.App.initElement = (el) => initPage(el);

// ─── HTMX boosted (SPA-like navigation) ──────────────────────────
//
// Après un swap HTMX boosted :
//   1. scroll to top
//   2. ré-init composants (sliders, accordéons, etc.)
//   3. ré-init AOS
//   4. lazyLoadInstance.update() pour scanner les nouveaux .lazy dans le DOM
//
document.body.addEventListener('htmx:afterSettle', (evt) => {
    if (!evt.detail.boosted) return;

    window.scrollTo({ top: 0, behavior: 'instant' });
    initPage(document);
    initAOS();
    initLazyLoad();   // update() ou création si première fois
});

// ── NOUVEAU : restauration depuis le cache historique ────────────
//
// Au retour navigateur (back/forward), htmx restaure le snapshot HTML mis
// en cache. Les éléments [data-aos] non encore animés portent toujours
// `aos-init` (invisibles via CSS) mais l'IntersectionObserver précédent
// est mort → ils ne reçoivent jamais `aos-animate`.
//
// Solution : re-init des composants + reset/refresh AOS.
//
document.body.addEventListener('htmx:historyRestore', () => {
    initPage(document);
    resetAOSForRestore(document);
    initLazyLoad();
});

// ─── Composants HTMX ─────────────────────────────────────────────
function initHTMXComponents(root = document) {
    root.querySelectorAll('[hx-confirm]').forEach(el => {
        if (el.dataset.htmxConfirmInit) return;
        el.dataset.htmxConfirmInit = '1';
        el.addEventListener('htmx:confirm', (evt) => {
            evt.preventDefault();
            if (confirm(el.getAttribute('hx-confirm') || 'Êtes-vous sûr ?')) {
                evt.detail.issueRequest();
            }
        });
    });

    root.querySelectorAll('[data-auto-submit]').forEach(el => {
        if (el.dataset.autoSubmitInit) return;
        el.dataset.autoSubmitInit = '1';
        el.addEventListener('change', () => htmx.trigger(el.closest('form'), 'submit'));
    });

    root.querySelectorAll('[data-toggle-class]').forEach(el => {
        if (el.dataset.toggleInit) return;
        el.dataset.toggleInit = '1';
        el.addEventListener('click', (e) => {
            e.preventDefault();
            const tgt = document.querySelector(
                el.getAttribute('data-toggle-target') || el.getAttribute('href')
            );
            const cls = el.getAttribute('data-toggle-class');
            if (tgt && cls) tgt.classList.toggle(cls);
        });
    });
}

document.addEventListener('htmx:load', () => window.Prism?.highlightAll());

console.log('📦 Front App loaded');